import React from 'react';
import {
  Avatar,
  Badge,
  Box,
  Flex,
  Group,
  Paper,
  Stack,
  Text,
  Title,
  UnstyledButton,
} from '@mantine/core';
import { IconArrowRight, IconUsers } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Eyebrow } from './primitives';

export interface ManagedResult {
  userId: string;
  name: string;
  avatar?: string;
}

interface ManagedResultsCardProps {
  people: ManagedResult[];
  onOpen: (userId: string) => void;
}

/**
 * Точка входа в саммари людей смотрящего: тех, за кого он отвечает, и тех, чей
 * он лид или PM. Строки появляются по одной, как только саммари предпубликовано
 * (сотрудник его ещё не видит), пока раунд собирает обратную связь по
 * остальным, — отсюда и идут на 1-on-1.
 */
export const ManagedResultsCard: React.FC<ManagedResultsCardProps> = ({ people, onOpen }) => {
  const { t } = useTranslation();

  return (
    <Paper withBorder p="xl" radius="md">
      <Flex justify="space-between" align="flex-end" mb="lg" gap="md">
        <Box>
          <Eyebrow>{t('perfReview.home.managed.section')}</Eyebrow>
          <Title order={3} size="h4" mt={6} style={{ letterSpacing: '-0.01em' }}>
            {t('perfReview.home.managed.title')}
          </Title>
          <Text size="sm" c="dimmed" mt={4}>
            {t('perfReview.home.managed.subtitle')}
          </Text>
        </Box>
        <Badge variant="light" color="teal" size="sm" leftSection={<IconUsers size={12} />}>
          {people.length}
        </Badge>
      </Flex>

      {/* Открывает саммари именно этого человека: общая кнопка внизу вела на
          список, где нужного ещё надо было найти. */}
      <Stack gap="xs">
        {people.map((p) => (
          <UnstyledButton
            key={p.userId}
            onClick={() => onOpen(p.userId)}
            aria-label={p.name}
            style={{ width: '100%', display: 'block' }}
          >
            <Paper
              p="sm"
              radius="md"
              withBorder
              style={{ background: 'var(--mantine-color-default-hover)' }}
            >
              <Group gap="sm" wrap="nowrap">
                <Avatar src={p.avatar} radius="xl" size={32}>
                  {p.name.slice(0, 1)}
                </Avatar>
                <Text size="sm" fw={500} style={{ flex: 1, minWidth: 0 }}>
                  {p.name}
                </Text>
                <Badge variant="light" color="teal" size="sm">
                  {t('perfReview.home.managed.ready')}
                </Badge>
                <IconArrowRight size={16} style={{ color: 'var(--mantine-color-dimmed)' }} />
              </Group>
            </Paper>
          </UnstyledButton>
        ))}
      </Stack>
    </Paper>
  );
};
